import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Trees, MapPin, Mountain, Building, Star } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getHomeBlurPlaceholder } from "@/utils/homeBlurPlaceholders";
import { BookingWidget } from "@/components/booking/BookingWidget";

// Video and fallback image paths - responsive videos
const heroVideoDesktop = "/videos/home-hero-720p-optimized.mp4";
const heroVideoMobile = "/videos/home-hero-mobile-ultra-optimized.mp4";

// Fallback image paths - home hero images
const heroFallbackImageDesktop = "/images/Homepage Shortlisted media/home hero image pc.webp";
const heroFallbackImageMobile = "/images/Homepage Shortlisted media/home hero image mobile.webp";

interface HeroCarouselState {
  videoLoaded: boolean;
  videoError: boolean;
  showFallback: boolean;
  showVideo: boolean;
  shouldLoadVideo: boolean;
}

interface HeroCarouselProps {
  autoPlayDelay?: number;
  transitionDuration?: number;
}

const HeroCarousel: React.FC<HeroCarouselProps> = ({
  autoPlayDelay = 3000, // 3 seconds as per specs
  transitionDuration = 300,
}) => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  
  // Use ref to store initial image source to prevent placeholder switching
  // Check window width directly on first render to avoid hydration mismatch
  const fallbackImageSrcRef = useRef<string>(
    typeof window !== "undefined" && window.innerWidth < 768
      ? heroFallbackImageMobile
      : heroFallbackImageDesktop
  );
  
  // Get the correct image source - use ref value to prevent switching after initial render
  const fallbackImageSrc = fallbackImageSrcRef.current;
  const [state, setState] = useState<HeroCarouselState>({
    videoLoaded: false,
    videoError: false,
    showFallback: true, // Start with fallback image
    showVideo: false,
    shouldLoadVideo: false, // Only load video after image is displayed
  });
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const mountTimeRef = useRef<number>(Date.now());

  // Video event handlers
  const handleVideoLoad = useCallback(() => {
    setState((prev) => ({ ...prev, videoLoaded: true, videoError: false }));
  }, []);

  const handleVideoError = useCallback((e: any) => {
    setState((prev) => ({
      ...prev,
      videoError: true,
      showFallback: true,
      showVideo: false,
    }));
  }, []);

  // Safari video autoplay fix - Programmatic play trigger (must be separate from visibility control)
  useEffect(() => {
    const playVideo = async () => {
      if (videoRef.current && state.shouldLoadVideo) {
        try {
          await videoRef.current.play();
        } catch (error) {
          console.log("Video autoplay prevented:", error);
        }
      }
    };

    // Try to play when video becomes available
    if (state.shouldLoadVideo && videoRef.current) {
      playVideo();

      // Also try when video metadata is loaded
      const video = videoRef.current;
      video.addEventListener("loadedmetadata", playVideo);
      video.addEventListener("canplay", playVideo);

      return () => {
        video.removeEventListener("loadedmetadata", playVideo);
        video.removeEventListener("canplay", playVideo);
      };
    }
  }, [state.shouldLoadVideo]);

  // Control video playback based on visibility
  useEffect(() => {
    if (videoRef.current) {
      if (state.showVideo && state.videoLoaded) {
        // Start playing when video becomes visible
        videoRef.current.play().catch(() => {
          // Video play failed silently
        });
      } else {
        // Pause when video is not visible
        videoRef.current.pause();
      }
    }
  }, [state.showVideo, state.videoLoaded]);

  // Start video loading immediately (no delay)
  useEffect(() => {
    // Trigger video load immediately to avoid render delay
    const timer = setTimeout(() => {
      setState((prev) => ({ ...prev, shouldLoadVideo: true }));
    }, 100); // Minimal delay just to ensure DOM is ready

    return () => clearTimeout(timer);
  }, []);

  // Transition to video when it's loaded and ready, but ensure image shows for at least 2 seconds
  useEffect(() => {
    if (state.shouldLoadVideo && state.videoLoaded && !state.videoError) {
      const elapsed = Date.now() - mountTimeRef.current;
      const minDisplayTime = 2000; // 2 seconds minimum display time
      const remainingTime = Math.max(0, minDisplayTime - elapsed);

      // Wait for remaining time (if any) plus small delay for smooth transition
      const timer = setTimeout(() => {
        setState((prev) => ({ ...prev, showVideo: true, showFallback: false }));
      }, remainingTime + 200);

      return () => clearTimeout(timer);
    }
  }, [state.shouldLoadVideo, state.videoLoaded, state.videoError]);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Touch handlers (kept for potential future use or scroll behavior)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback(() => {
    // Touch handling can be used for scroll behavior or other interactions
    // Currently not needed for video background
  }, [touchStart, touchEnd]); // Keyboard navigation removed since we're using video background

  const videoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": "Beatenberg Resort & Spa Experience",
    "description": "Experience the rustic luxury of Beatenberg Resort & Spa in the Swiss Alps, featuring panoramic views of the Eiger, Mönch, and Jungfrau.",
    "thumbnailUrl": [
      `https://beatenberg-resortspa.ch${heroFallbackImageDesktop}`,
      `https://beatenberg-resortspa.ch${heroFallbackImageMobile}`
    ],
    "uploadDate": "2024-01-01T08:00:00+01:00",
    "contentUrl": `https://beatenberg-resortspa.ch${heroVideoDesktop}`,
    "embedUrl": `https://beatenberg-resortspa.ch${heroVideoDesktop}`,
    "publisher": {
      "@type": "Organization",
      "name": "Beatenberg Resort & Spa",
      "logo": {
        "@type": "ImageObject",
        "url": "https://beatenberg-resortspa.ch/images/phb-icon-180.png"
      }
    }
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(videoSchema)}
        </script>
      </Helmet>
      <style data-oid="0742tb6">{`
        @media (min-width: 1200px) and (max-height: 700px) {
          .hidden-wide-short {
            display: none !important;
          }
        }
        @media (min-width: 1300px) and (max-height: 650px) {
          .hidden-wide-short {
            display: none !important;
          }
        }
        @media (min-width: 1400px) and (max-height: 600px) {
          .hidden-wide-short {
            display: none !important;
          }
        }
      `}</style>
      <div
        ref={carouselRef}
        className="fixed top-0 left-0 right-0 h-screen w-full overflow-hidden z-10"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        data-oid="0ee.4go"
      >
        {/* Video/Image Container - Full screen background */}
        <div className="absolute inset-0" data-oid="camy_rf">
          {/* Fallback Image - Shows first, then transitions to video */}
          <motion.div
            className="absolute inset-0 z-0"
            initial={{ opacity: 1 }}
            animate={{ opacity: state.showFallback ? 1 : 0 }}
            transition={{
              duration: transitionDuration / 1000,
              ease: "easeInOut",
            }}
            data-oid="6a8kl9x"
          >
            <OptimizedImage
              src={fallbackImageSrc}
              alt="Beatenberg Resort & Spa - Alpine Views"
              className="w-full h-full object-cover"
              priority={true}
              fetchPriority="high"
              blurDataURL={getHomeBlurPlaceholder(fallbackImageSrc)}
              hideLoadingSpinner={true}
              data-oid="ggy5cyc"
            />
          </motion.div>

          {/* Video Background - Loads conditionally after image is displayed */}
          <motion.div
            className="absolute inset-0 z-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: state.showVideo ? 1 : 0 }}
            transition={{
              duration: transitionDuration / 1000,
              ease: "easeInOut",
            }}
            data-oid="f6yq69g"
          >
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload={state.shouldLoadVideo ? "metadata" : "none"}
              onLoadedData={handleVideoLoad}
              onCanPlay={handleVideoLoad}
              onError={handleVideoError}
              style={{ opacity: state.showVideo ? 1 : 0 }}
              data-oid="wtub1_x"
            >
              <source
                src={heroVideoDesktop}
                type="video/mp4"
                media="(min-width: 769px)"
                data-oid="cu3d-82"
              />
              <source
                src={heroVideoMobile}
                type="video/mp4"
                media="(max-width: 768px)"
                data-oid="_l.9pai"
              />
              {/* Fallback message if video format is not supported */}
              Your browser does not support the video tag.
            </video>
          </motion.div>

          {/* Fade Overlay for smoother transitions */}
          <div
            className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20 z-5"
            data-oid="q8sp60a"
          />

          {/* Light dark overlay for text readability */}
          <div
            className="absolute inset-0 bg-black/15 z-10"
            data-oid="w6n6d9k"
          />
        </div>

        {/* Content Overlay - Show immediately for fast LCP */}
        <div
          className="absolute inset-0 z-20 flex flex-col justify-center items-center py-8 md:py-12 lg:py-16 px-4"
          style={{ paddingTop: "clamp(2rem, 8vh, 4rem)" }}
          data-oid="rrascc1"
        >
          {/* Logo Section - Top */}
          <motion.div
            className="flex-shrink-0 flex flex-col justify-center items-center w-full max-w-7xl mx-auto mb-8 md:mb-12 lg:mb-16"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0 }}
            data-oid=".jau084"
          >
            <OptimizedImage
              src="/images/updated logo.svg"
              alt="Beatenberg Resort & Spa Logo"
              className="opacity-90 mb-4 hidden-wide-short rounded-none w-auto"
              style={{
                filter: "drop-shadow(0 0 8px rgba(255,255,255,0.3))",
                height: "clamp(6rem, 20vw, 12rem)",
                maxHeight: "clamp(6rem, 20vw, 12rem)",
                maxWidth: "90vw"
              }}
              priority={true}
              fetchPriority="high"
              blurDataURL={getHomeBlurPlaceholder("/images/updated logo.svg")}
              hideLoadingSpinner={true}
              data-oid="8.1x-iv"
            />

            <div className="text-center" data-oid="zzpfe8p">
              {/* Changed from h1 to div for SEO - only one h1 per page */}
              <div
                className="font-playfair text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white mb-1"
                style={{
                  letterSpacing: "0.05em",
                  textShadow:
                    "0 0 20px rgba(0,0,0,0.8), 0 2px 4px rgba(0,0,0,0.6)",
                  fontSize: "clamp(1.5rem, 4vw, 4rem)",
                }}
                role="img"
                aria-label={t("hero.title")}
                data-oid="ik3fsfa"
              >
                {t("hero.title")}
              </div>
              <h2
                className="font-playfair text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl text-white mb-3"
                style={{
                  letterSpacing: "0.1em",
                  textShadow:
                    "0 0 20px rgba(0,0,0,0.8), 0 2px 4px rgba(0,0,0,0.6)",
                  fontSize: "clamp(1.125rem, 2.5vw, 2.5rem)",
                }}
                data-oid="qdju:c5"
              >
                {t("hero.subtitle")}
              </h2>

              <div
                className="flex flex-col items-center space-y-2"
                data-oid="2x6m72h"
              >
                <div className="flex space-x-1" data-oid="gn81ela">
                  {[...Array(4)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      fill="#FFFFFF"
                      color="#FFFFFF"
                      style={{
                        filter: "drop-shadow(0 0 15px rgba(0,0,0,0.6))",
                      }}
                      data-oid="q-muc:u"
                    />
                  ))}
                </div>
                <OptimizedImage
                  src="/images/logo gastrosuisse.svg"
                  alt="Gastrosuisse Logo"
                  className="h-5 sm:h-6 md:h-8 lg:h-10 w-auto opacity-90 mb-2"
                  style={{
                    filter: "drop-shadow(0 0 8px rgba(255,255,255,0.3))",
                  }}
                  priority={true}
                  fetchPriority="high"
                  blurDataURL={getHomeBlurPlaceholder("/images/logo gastrosuisse.svg")}
                  hideLoadingSpinner={true}
                  data-oid="eoj86.b"
                />
              </div>
            </div>
          </motion.div>

          {/* Badge Container - Higher Position */}
          <motion.div
            className="flex-shrink-0 flex flex-col items-center space-y-3 w-full max-w-7xl mx-auto"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0 }}
            data-oid="lfwob.-"
          >
            {/* Secondary Badges */}
            <div
              className="flex flex-wrap gap-4 justify-center items-center w-full"
              style={{ filter: "drop-shadow(0 0 20px rgba(0,0,0,0.8))" }}
              data-oid="vyavc4e"
            >
              <span
                className="flex items-center gap-2 text-white text-sm sm:text-base md:text-lg lg:text-xl font-medium uppercase tracking-wider whitespace-nowrap"
                style={{ textShadow: "0 0 30px rgba(0,0,0,0.6)" }}
                data-oid="9c98327"
              >
                <Trees size={18} data-oid="ndpogm0" />
                {t("hero.acreResort")}
              </span>
              <div
                className="hidden sm:block w-[1.5px] h-5 bg-white/40"
                style={{ textShadow: "0 0 10px rgba(0,0,0,0.3)" }}
                data-oid="utlhcqa"
              ></div>
              <span
                className="flex items-center gap-2 text-white text-sm sm:text-base md:text-lg lg:text-xl font-medium uppercase tracking-wider whitespace-nowrap"
                style={{ textShadow: "0 0 30px rgba(0,0,0,0.6)" }}
                data-oid="n3cn0rf"
              >
                <MapPin size={18} data-oid="m8kwcaf" />
                {t("hero.minsFromInterlaken")}
              </span>
              <div
                className="hidden sm:block w-[1.5px] h-5 bg-white/40"
                style={{ textShadow: "0 0 10px rgba(0,0,0,0.3)" }}
                data-oid="zzzkixk"
              ></div>
              <span
                className="flex items-center gap-2 text-white text-sm sm:text-base md:text-lg lg:text-xl font-medium uppercase tracking-wider whitespace-nowrap"
                style={{ textShadow: "0 0 30px rgba(0,0,0,0.6)" }}
                data-oid="nkmxc9:"
              >
                <Mountain size={18} data-oid="s7ljfn2" />
                {t("hero.stunningAlpineViews")}
              </span>
              <div
                className="hidden sm:block w-[1.5px] h-5 bg-white/40"
                style={{ textShadow: "0 0 10px rgba(0,0,0,0.3)" }}
                data-oid="6o3bleu"
              ></div>
              <span
                className="flex items-center gap-2 text-white text-sm sm:text-base md:text-lg lg:text-xl font-medium uppercase tracking-wider whitespace-nowrap"
                style={{ textShadow: "0 0 30px rgba(0,0,0,0.6)" }}
                data-oid="7r2cn7k"
              >
                <Building size={18} data-oid="ej3n-w5" />
                {t("hero.since1881")}
              </span>
            </div>

            {/* Set Label - Below secondary badges */}
            <div className="mt-2 sm:mt-3 lg:mt-4" data-oid="3v6ks5o">
              <div
                className="bg-black/30 text-white px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium uppercase tracking-widest"
                data-oid="0gw3s1n"
              >
                {t("hero.description")}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Booking Widget Overlay - Desktop Only */}
        <div className="absolute bottom-8 left-0 right-0 z-30 hidden md:block">
          <BookingWidget />
        </div>

        {/* Navigation Indicators removed - using video background */}
      </div>
    </>
  );
};

export default HeroCarousel;
